'use client'

import StackTrace from 'stacktrace-js'

/**
 * Client Error Reporter — the single path from a browser error to the server log.
 *
 * Every client-side error entry point calls reportClientError() from here:
 *
 *   app/error.js                    type: 'route'  (route segment boundary)
 *   app/global-error.js             type: 'global' (root layout crash)
 *   app/_components/ErrorBoundary   type: 'react'  (client component tree)
 *   useErrorReporting               type: 'window' | 'unhandledrejection'
 *
 * Reports land in the same pino stream as server errors, tagged
 * CLIENT_ERROR:RECEIVED. Grep that in logs/pm2-out.log.
 *
 * This file exists because the reporting logic was previously copy-pasted
 * across three of those four files with three different payload shapes, and
 * omitted entirely from the fourth — so route-level render errors were
 * invisible in the logs for months. Add new entry points here, never inline.
 */

const ENDPOINT = '/api/log-client-error'

/** Stop a render loop from flooding the endpoint. */
const MAX_REPORTS_PER_SESSION = 25
const MAX_REPORTS_PER_SIGNATURE = 3

const reportCounts = new Map()
let totalReports = 0

/**
 * Session state used to give every report the same diagnostic context.
 *
 * Module-level rather than React refs so that all four entry points share one
 * view of the session. useErrorReporting feeds it; everyone benefits.
 */
const session = {
    startTime: null,
    pageLoadTime: null,
    navigationHistory: [],
    lastUserAction: null,
}

/**
 * The client is running code from a build that no longer exists — it cannot
 * load a chunk the current server does not serve. A reload always fixes this
 * and cannot loop, because the reload fetches the new build.
 */
export function isStaleChunkError(error) {
    const message = error?.message || ''
    const name = error?.name || ''

    return (
        name === 'ChunkLoadError' ||
        message.includes('ChunkLoadError') ||
        message.includes('Loading chunk') ||
        message.includes('Failed to load dynamically imported module')
    )
}

/**
 * Stale chunks plus transient RSC/network failures that a reload usually fixes.
 *
 * Deliberately NOT used by boundaries that reload from inside render: a
 * persistent 'Failed to fetch' there would reload-loop. Only the top-level
 * boundaries (error.js, global-error.js) use this, matching their existing
 * production behaviour.
 */
export function isStaleClientError(error) {
    const message = error?.message || ''

    return (
        isStaleChunkError(error) ||
        message.includes('Connection closed') ||
        message.includes('Failed to fetch') ||
        message.includes('Load failed') ||
        message.includes('NEXT_STATIC_GEN_BAILOUT')
    )
}

/** Start the session clock. Safe to call repeatedly. */
export function markSessionStart() {
    if (session.startTime === null) {
        session.startTime = Date.now()
    }
}

/** Record a navigation so error reports carry the route history that led to them. */
export function recordNavigation(pathname) {
    const now = Date.now()

    session.navigationHistory = [
        ...session.navigationHistory.slice(-9),
        { path: pathname, timestamp: now },
    ]
    session.pageLoadTime = now
}

/** Record the last thing the user did before an error, for reproduction steps. */
export function recordUserAction(action) {
    session.lastUserAction = { ...action, timestamp: Date.now() }
}

/**
 * Decode a minified stack trace with source maps.
 *
 * Errors thrown inside React internals (flight client, reconciler) arrive with
 * nothing but single-letter frames, so the raw stack alone is near useless.
 */
export async function decodeStack(error) {
    if ( ! error) return 'No error object available'

    try {
        const frames = await StackTrace.fromError(error)

        return frames.map(frame => {
            const fileName = frame.fileName || 'unknown'
            const lineNumber = frame.lineNumber || '?'
            const columnNumber = frame.columnNumber || '?'
            const functionName = frame.functionName || '(anonymous)'
            return `    at ${functionName} (${fileName}:${lineNumber}:${columnNumber})`
        }).join('\n')
    } catch {
        // Source map fetch failed - raw stack is better than nothing
        return error.stack || 'Stack trace unavailable'
    }
}

/**
 * Collect browser/environment diagnostics.
 *
 * Guarded throughout: this runs while the app is already broken, and a throw
 * here would lose the report we are trying to send.
 */
function collectDiagnostics() {
    try {
        const ua = navigator.userAgent
        const now = Date.now()

        const diagnostics = {
            // Browser
            safariVersion: /Version\/(\d+\.\d+(\.\d+)?)/.exec(ua)?.[1] || null,
            webkitVersion: /AppleWebKit\/(\d+\.\d+)/.exec(ua)?.[1] || null,
            chromeVersion: /Chrome\/(\d+\.\d+)/.exec(ua)?.[1] || null,
            firefoxVersion: /Firefox\/(\d+\.\d+)/.exec(ua)?.[1] || null,

            // OS
            macOSVersion: /Mac OS X (\d+[._]\d+([._]\d+)?)/.exec(ua)?.[1]?.replace(/_/g, '.') || null,
            iOSVersion: /OS (\d+_\d+(_\d+)?)/.exec(ua)?.[1]?.replace(/_/g, '.') || null,

            // Device
            deviceMemory: navigator.deviceMemory || 'unknown',
            hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
            maxTouchPoints: navigator.maxTouchPoints || 0,

            // Connection
            connectionType: navigator.connection?.effectiveType || 'unknown',
            connectionDownlink: navigator.connection?.downlink || 'unknown',
            onLine: navigator.onLine,

            // Page state
            documentVisibilityState: document.visibilityState,
            documentReadyState: document.readyState,
            wasPageRestored: performance.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward',

            // Timing
            sessionDurationMs: session.startTime ? now - session.startTime : null,
            timeOnPageMs: session.pageLoadTime ? now - session.pageLoadTime : null,
            pageLoadTimestamp: session.pageLoadTime ? new Date(session.pageLoadTime).toISOString() : null,

            // Navigation context
            navigationHistoryCount: session.navigationHistory.length,
            navigationHistory: session.navigationHistory.map(n => ({
                path: n.path,
                secondsAgo: Math.round((now - n.timestamp) / 1000),
            })),

            lastUserAction: session.lastUserAction ? {
                ...session.lastUserAction,
                secondsAgo: Math.round((now - session.lastUserAction.timestamp) / 1000),
            } : null,

            // Memory (Chrome only)
            jsHeapSizeLimit: performance.memory?.jsHeapSizeLimit || null,
            usedJSHeapSize: performance.memory?.usedJSHeapSize || null,
            totalJSHeapSize: performance.memory?.totalJSHeapSize || null,

            // Document
            activeElementTag: document.activeElement?.tagName?.toLowerCase() || null,
            documentHasFocus: document.hasFocus(),
            windowInnerWidth: window.innerWidth,
            windowInnerHeight: window.innerHeight,
            devicePixelRatio: window.devicePixelRatio,

            performanceNow: Math.round(performance.now()),

            potentialIssues: [],
        }

        if (diagnostics.wasPageRestored) {
            diagnostics.potentialIssues.push('Page was restored from back/forward cache (bfcache)')
        }
        if (diagnostics.documentVisibilityState === 'hidden') {
            diagnostics.potentialIssues.push('Page was hidden when error occurred')
        }
        if (diagnostics.sessionDurationMs > 3600000) {
            diagnostics.potentialIssues.push('Long session (>1 hour)')
        }
        if (diagnostics.navigationHistoryCount > 5) {
            diagnostics.potentialIssues.push('Heavy navigation (5+ pages this session)')
        }
        if (diagnostics.usedJSHeapSize && diagnostics.jsHeapSizeLimit) {
            const heapUsage = diagnostics.usedJSHeapSize / diagnostics.jsHeapSizeLimit
            if (heapUsage > 0.8) {
                diagnostics.potentialIssues.push(`High memory usage (${Math.round(heapUsage * 100)}%)`)
            }
        }

        return diagnostics
    } catch {
        return {}
    }
}

/**
 * True when this signature has already been reported enough times.
 * Keeps a render loop from turning one bug into thousands of log lines.
 */
function isSuppressed(signature) {
    if (totalReports >= MAX_REPORTS_PER_SESSION) return true

    const count = reportCounts.get(signature) || 0
    if (count >= MAX_REPORTS_PER_SIGNATURE) return true

    reportCounts.set(signature, count + 1)
    totalReports++

    return false
}

/**
 * Report a client-side error to the server log.
 *
 * @param {Object}  options
 * @param {Error}   [options.error]          The error object, when available
 * @param {string}  options.type             'route' | 'global' | 'react' | 'window' | 'unhandledrejection'
 * @param {string}  [options.message]        Overrides error.message (window events carry their own)
 * @param {string}  [options.rawStack]       Overrides error.stack
 * @param {string}  [options.componentStack] React component stack, when the caller has one
 * @returns {Promise<void>} Never rejects.
 */
export async function reportClientError({ error, type, message, rawStack, componentStack } = {}) {
    try {
        const resolvedMessage = message || error?.message || 'Unknown error'
        const signature = `${type}::${resolvedMessage}`.slice(0, 200)

        if (isSuppressed(signature)) return

        const stack = error instanceof Error ? await decodeStack(error) : ''

        await fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                type,
                message: resolvedMessage,
                stack,
                rawStack: rawStack || error?.stack || '',
                componentStack: componentStack || null,
                // The only key React gives us to tie a client-visible error
                // back to the server log line that produced it
                digest: error?.digest || null,
                url: typeof window !== 'undefined' ? window.location.href : '',
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                timestamp: new Date().toISOString(),
                diagnostics: collectDiagnostics(),
            }),
        })
    } catch (e) {
        // Reporting must never become the error it is reporting
        console.error('[clientErrorReporter] Failed to report error:', e)
    }
}
